/**
 * `useLiveCollection` — тонкая Vue-обёртка над `live-collection-core.ts`.
 *
 * Общий механизм «events + периодический refresh» для виджетов, держащих в
 * памяти ОГРАНИЧЕННЫЙ живой набор строк CRUD-сущности (диалоги, операторы,
 * активные звонки, — данные, которые реалистично целиком помещаются в память
 * и живут «пока открыт виджет»).
 *
 * ⚠ НЕ для архивов с пагинацией (CDR, история сообщений и т.п.) — там живой
 * набор (Map «всё, что когда-либо подошло под фильтр») воюет с `limit`:
 * событие вне текущей страницы всё равно улетит в Map, а refresh() каждый раз
 * тянет `limit` строк заново, не «следующую страницу». Для постраничных
 * списков — обычный `q.<entity>.list` без live-слоя.
 *
 * Тип строки задаётся вторым generic-параметром явно
 * (`useLiveCollection<typeof SessionEntity, SessionRow>`): вывести его из
 * `entity.routes` не выйдет — констрейнт намеренно свободный (см.
 * `LiveCollectionEntity.routes`), а `SnapshotOf<R>` матчит `CrudRoutesShape`,
 * которому реальные сущности под `exactOptionalPropertyTypes` не удовлетворяют.
 * Дублирования нет: тип строки у потребителя уже под рукой (`SessionRow` и т.п.
 * экспортируются из `entities/<x>/api`, где они и так выведены из контракта).
 */
import { computed, onScopeDispose, ref, watch, type ComputedRef, type Ref } from 'vue';
import type { CrudFilterAst, CrudProjectionFieldOf, CrudSortEntry } from '@omniflow/contracts-base';
import { projectSnapshot } from '@omniflow/contracts-base';
import type { InputOf, MessageFields } from '@omniflow/contracts-core';
import type { AnyEventRoute, AnyRequestRoute } from './nats-bus';
import { useNatsStore } from './nats-store';
import {
  applyLiveEvent,
  compileRowFilter,
  readEntityRow,
  sortRows,
  type LiveRowLike,
} from './live-collection-core';

/**
 * Структурное ограничение на «сущность» — ровно то, что дёргает композабл.
 * Результат `defineEntity(...)` ему удовлетворяет (см. поле `routes` ниже).
 */
export interface LiveCollectionEntity {
  name: string;
  /**
   * Ровно ЧЕТЫРЕ routes, которые нужны живой коллекции, типизированные ровно так,
   * как их требует шина (`AnyRequestRoute`/`AnyEventRoute` из nats-bus) — не больше.
   *
   * ⚠ Здесь НЕЛЬЗЯ использовать `CrudRoutesShape` из contracts-base как констрейнт,
   * хотя он и описывает ту же 8-route форму. Причина (поймана первым боевым
   * применением 15.07): его reply-формы объявлены как `error?: string`, а zod у
   * ЛЮБОЙ реальной сущности даёт `error?: string | undefined` (`z.optional()` всегда
   * добавляет explicit undefined). В apps/ui включён `exactOptionalPropertyTypes:
   * true`, где это НЕ одно и то же → `SessionEntity` не удовлетворяет констрейнту
   * (падает на `routes.update.replySchema._type`), и каждый потребитель вынужден
   * писать `as unknown as`-каст. Параметрами типа не лечится: несовместимость в
   * НЕпараметризованных reply (update/delete/get).
   *
   * Отсюда правило: констрейнт описывает лишь то, что композабл реально дёргает.
   * Полную форму сущности он не проверяет и проверять не должен — за неё отвечает
   * `defineEntity`.
   *
   * `created`/`deleted` необязательны ради списков вне CRUD-стандарта
   * (`q.admin.users.list`): новое и изменённое там приходит одним `updated`.
   */
  routes: {
    list: AnyRequestRoute;
    created?: AnyEventRoute;
    updated: AnyEventRoute;
    deleted?: AnyEventRoute;
  };
}

export interface UseLiveCollectionOptions<
  E extends LiveCollectionEntity,
  TRow extends LiveRowLike,
> {
  /** Результат `defineEntity(...)` (или объект с той же публичной формой). */
  entity: E;
  /**
   * Один AST — уходит и в `q.<entity>.list` (серверная выборка), и в
   * `compileFilter` (проверка live-событий: строка ещё принадлежит коллекции?).
   * Реактивный — смена значения триггерит recompile предиката + refresh().
   */
  filter?: Ref<CrudFilterAst<string> | undefined>;
  /** Серверная сортировка (в запрос `q.<entity>.list`). */
  sort?: CrudSortEntry<string>[];
  limit?: number;
  /** Клиентский порядок строк после live-правок (между refresh()). Без него — порядок Map. */
  compare?: (a: TRow, b: TRow) => number;
  /** Интервал poll-fallback (страховка от пропущенных событий/дрейфа). Дефолт 60с. */
  pollMs?: number;
  /** Хук на применённое live-изменение (не 'ignored') — для бейджей/автоскролла. `row` = null для hard-delete (payload не несёт снимка). */
  onChange?: (kind: 'added' | 'updated' | 'removed', row: TRow | null) => void;
  /**
   * Хук «снимок перезагружен» — зовётся после КАЖДОГО успешного refresh (первый,
   * poll, реконнект, смена фильтра) со свежими строками.
   *
   * Нужен потому, что `loading`/`firstLoad` не отвечают на вопрос «мой ли это
   * refresh закончился»: потребителю (лента чата) нужна детерминированная точка
   * после смены диалога — доскроллить вниз и отметить прочитанным. Без хука
   * приходилось звать `refresh()` вторым, лишним запросом (поймано первым боевым
   * применением, 15.07).
   */
  onRefreshed?: (rows: TRow[]) => void;
  /**
   * CRUDPROJ: сузить строки списка до этого набора полей (XOR с `fieldsOmit` —
   * энфорсит сервер). Прокидывается в `q.<entity>.list`. Live-события (полные
   * snapshot'ы) проецируются тем же набором client-side (`projectSnapshot`) —
   * коллекция остаётся гомогенна с bootstrap'ом. Фильтр (`opts.filter`) видит
   * ПОЛНУЮ строку события, проекция применяется уже после матча.
   *
   * `id`/`updatedAt` добавляются в набор принудительно (I4: без `updatedAt`
   * anti-stale-guard живой коллекции слеп).
   */
  fields?: readonly CrudProjectionFieldOf<TRow>[];
  /**
   * CRUDPROJ: обратный вариант — список полей, которые нужно ВЫРЕЗАТЬ (XOR с `fields`).
   * `id`/`updatedAt` из набора молча вычёркиваются (форс-ключи, см. `fields`).
   */
  fieldsOmit?: readonly CrudProjectionFieldOf<TRow>[];
}

export interface UseLiveCollectionResult<TRow> {
  rows: Ref<TRow[]>;
  byId: ComputedRef<Map<string, TRow>>;
  loading: Ref<boolean>;
  error: Ref<string | null>;
  firstLoad: Ref<boolean>;
  refresh: () => Promise<void>;
}

export const useLiveCollection = <
  E extends LiveCollectionEntity,
  TRow extends LiveRowLike,
>(
  opts: UseLiveCollectionOptions<E, TRow>,
): UseLiveCollectionResult<TRow> => {
  const natsStore = useNatsStore();

  const loading = ref(false);
  const firstLoad = ref(true);
  const error = ref<string | null>(null);

  /** Map — ВНЕ реактивности (как в эталоне pulse-виджета): O(1) upsert/delete, наружу отдаём пересобранный ref-массив. */
  const byId = new Map<string, TRow>();
  const rows = ref<TRow[]>([]) as Ref<TRow[]>;

  const filterRef: Ref<CrudFilterAst<string> | undefined> = opts.filter ?? ref(undefined);
  const predicate = computed(() => compileRowFilter<TRow>(filterRef.value));

  /**
   * CRUDPROJ: та же проекция, что ушла в `q.<entity>.list`, применяется к
   * live-событиям client-side — `fields`/`fieldsOmit` в опциях статичны на
   * протяжении жизни композабла (не Ref), поэтому один расчёт достаточен.
   */
  /**
   * I4: `updatedAt` — форс-ключ живой коллекции (как `id`/`tenantId` на
   * сервере). Без него anti-stale-guard (`isStale` в live-collection-core)
   * сравнивать нечего: запоздалое событие после WS-реконнекта перетёрло бы
   * свежую строку. Поэтому при `fields` ключи ДОБАВЛЯЮТСЯ, при `fieldsOmit` —
   * молча ВЫЧЁРКИВАЮТСЯ из omit-набора.
   */
  const LIVE_FORCED_FIELDS = ['id', 'updatedAt'] as const;
  const forcedFields = (fields: readonly string[]): string[] => [
    ...new Set([...fields, ...LIVE_FORCED_FIELDS]),
  ];
  const forcedOmit = (fieldsOmit: readonly string[]): string[] =>
    fieldsOmit.filter((field) => !LIVE_FORCED_FIELDS.includes(field as (typeof LIVE_FORCED_FIELDS)[number]));

  /** Нормализованные наборы — уходят И в dto списка, И в проекцию live-событий. */
  const effectiveFields = opts.fields !== undefined ? forcedFields(opts.fields as readonly string[]) : undefined;
  const effectiveOmitRaw = opts.fieldsOmit !== undefined ? forcedOmit(opts.fieldsOmit as readonly string[]) : undefined;
  // Пустой omit-набор (вычеркнули всё) = проекции нет: серверная схема требует min(1).
  const effectiveOmit = effectiveOmitRaw !== undefined && effectiveOmitRaw.length > 0 ? effectiveOmitRaw : undefined;

  const projection =
    effectiveFields !== undefined || effectiveOmit !== undefined
      ? {
          ...(effectiveFields !== undefined ? { fields: effectiveFields } : {}),
          ...(effectiveOmit !== undefined ? { fieldsOmit: effectiveOmit } : {}),
        }
      : null;
  const transformRow =
    projection !== null
      ? (row: TRow): TRow => projectSnapshot(row as Record<string, unknown>, projection) as TRow
      : null;

  const rebuildRows = (): void => {
    rows.value = sortRows([...byId.values()], opts.compare);
  };

  const buildListDto = (): Omit<InputOf<E['routes']['list']>, MessageFields> => {
    const dto: Record<string, unknown> = {};
    if (filterRef.value !== undefined) dto.filter = filterRef.value;
    if (opts.limit !== undefined) dto.limit = opts.limit;
    if (opts.sort !== undefined) dto.sort = opts.sort;
    // I4: в запрос уходит тот же нормализованный набор, что и в проекцию
    // live-событий (с форс-ключами id/updatedAt) — иначе bootstrap и live
    // разошлись бы по форме строки, а anti-stale-guard остался бы слепым.
    if (effectiveFields !== undefined) dto.fields = effectiveFields;
    if (effectiveOmit !== undefined) dto.fieldsOmit = effectiveOmit;
    // Единственное место во всём модуле с явным (задокументированным) кастом.
    // Публичный `CrudRoutesShape.list` (contracts-base/routes-shape.ts) типизирует
    // Input ТОЛЬКО как `MessageWithTypeVersion` (без filter/limit/sort — это
    // намеренно широкий satisfies-контракт, конкретную bounded-list-форму
    // (`filter`/`limit`/`offset`/`sort` с literal-enum'ом полей сущности) несёт
    // только внутренний тип конкретного route'а, недоступный через generic E,
    // (констрейнт держит `list` как `AnyRequestRoute` — того требует шина). Значит и
    // `InputOf<E['routes']['list']>` для generic E тоже вырождается почти в `{}` —
    // статически точнее не типизировать. Рантайм безопасен: сервер валидирует
    // filter/limit/sort zod-схемой конкретного route'а независимо от того, что
    // здесь думает TS.
    return dto as Omit<InputOf<E['routes']['list']>, MessageFields>;
  };

  /**
   * Epoch-guard конкурентных refresh(): быстрое переключение фильтра (диалог
   * A → B) даёт два запроса в полёте, и БЕЗ guard'а коллекцию заполнял бы тот
   * ответ, что пришёл ПОЗЖЕ по сети — не тот, что запрошен ПОСЛЕДНИМ (поймано
   * ночным аудитом 16.07). Применяем только ответ актуальной эпохи.
   */
  let refreshEpoch = 0;

  const refresh = async (): Promise<void> => {
    const bus = natsStore.bus;
    if (!bus) return;
    const epoch = ++refreshEpoch;
    loading.value = true;
    try {
      // Каст reply — цена свободного констрейнта: `AnyRequestRoute` (то, что
      // требует шина) объявляет reply как `unknown`, а конкретный тип route'а из
      // generic E недоступен. Форму list-reply гарантирует CRUD-стандарт
      // (`crudBoundedListReplyBody`), рантайм её валидирует zod'ом на сервере —
      // здесь лишь сообщаем TS то, что и так инвариант платформы. Вне стандарта
      // (`q.admin.users.list`) поля `isOk` нет: там ошибка приходит исключением.
      const reply = (await bus.request(opts.entity.routes.list, buildListDto())) as
        | { isOk?: true; items: TRow[]; total: number }
        | { isOk: false; errorCode: string; error?: string };
      // Пока ждали ответ, стартовал более свежий refresh (смена фильтра/поллинг) —
      // этот снимок устарел, применять нельзя (перетёр бы данные новой эпохи).
      if (epoch !== refreshEpoch) return;
      if (reply.isOk === false) {
        error.value = reply.error ?? reply.errorCode;
        console.error(`useLiveCollection(${opts.entity.name}): list failed`, reply.errorCode, reply.error);
        return;
      }
      error.value = null;
      byId.clear();
      for (const item of reply.items) byId.set(item.id, item);
      rebuildRows();
      // Детерминированная точка «снимок актуален» для потребителя (доскроллить,
      // отметить прочитанным). Хук не должен ронять refresh — он сам зовётся из
      // таймера/реконнекта, где throw некому ловить.
      try {
        opts.onRefreshed?.(rows.value);
      } catch (hookErr) {
        console.error(`useLiveCollection(${opts.entity.name}): onRefreshed failed`, hookErr);
      }
    } catch (err) {
      // НЕ re-throw: refresh() зовётся и из poll-таймера, и из watch на реконнект —
      // re-throw заспамил бы глобальные нотификации на каждый обрыв связи.
      if (epoch !== refreshEpoch) return; // сбой устаревшей эпохи не перетирает статус свежей
      error.value = err instanceof Error ? err.message : `Ошибка загрузки ${opts.entity.name}`;
      console.error(`useLiveCollection(${opts.entity.name}): refresh failed`, err);
    } finally {
      // loading/firstLoad принадлежат АКТУАЛЬНОМУ запросу: устаревший, финишируя
      // позже, не должен гасить спиннер живого.
      if (epoch === refreshEpoch) {
        loading.value = false;
        firstLoad.value = false;
      }
    }
  };

  const handleUpsertEvent = (kind: 'created' | 'updated', payload: unknown): void => {
    const result = applyLiveEvent(byId, kind, payload, {
      entityName: opts.entity.name,
      predicate: predicate.value,
      ...(transformRow !== null ? { transformRow } : {}),
    });
    if (result === 'ignored') return;
    rebuildRows();
    // `byId` несёт УЖЕ спроецированную строку (transformRow применился перед
    // записью в Map внутри applyLiveEvent) — onChange должен отдавать то же,
    // что легло в коллекцию, не сырой payload события.
    const row = readEntityRow<TRow>(payload, opts.entity.name);
    const storedRow = row !== null ? (byId.get(row.id) ?? null) : null;
    opts.onChange?.(result, result === 'removed' ? null : storedRow);
  };

  const handleDeletedEvent = (payload: unknown): void => {
    const result = applyLiveEvent(byId, 'deleted', payload, {
      entityName: opts.entity.name,
      predicate: predicate.value,
    });
    if (result === 'ignored') return;
    rebuildRows();
    opts.onChange?.(result, null);
  };

  let unsubscribeEvents: (() => void) | null = null;

  const subscribeToBus = (): void => {
    unsubscribeEvents?.();
    unsubscribeEvents = null;

    const bus = natsStore.bus;
    if (!bus) return;

    const { created, updated, deleted } = opts.entity.routes;
    const subscriptions = [
      bus.subscribe(updated, (payload) => handleUpsertEvent('updated', payload)),
      ...(created ? [bus.subscribe(created, (payload) => handleUpsertEvent('created', payload))] : []),
      ...(deleted ? [bus.subscribe(deleted, (payload) => handleDeletedEvent(payload))] : []),
    ];

    unsubscribeEvents = () => {
      for (const subscription of subscriptions) subscription.unsubscribe();
    };
  };

  // Bus может стать доступным ПОСЛЕ создания композабла (гонка с NATS-коннектом
  // при старте приложения) либо смениться на новый инстанс (logout/relogin —
  // natsStore.connect() создаёт новый NatsBus). Оба случая — пересоздать подписки
  // и перечитать снимок: события за время без шины потеряны, а опроса ждать до pollMs.
  watch(
    () => natsStore.bus,
    () => {
      subscribeToBus();
      void refresh();
    },
    { immediate: true },
  );

  // Реконнект — ключевой источник дрейфа: подписки NATS не буферизуют события,
  // случившиеся между disconnect и reconnect. На СПАДЕ флага (было true → стало
  // false, т.е. реконнект успешно завершился) сразу подтягиваем актуальное
  // состояние запросом, не дожидаясь poll-таймера (до pollMs, дефолт 60с).
  watch(
    () => natsStore.reconnecting,
    (isReconnecting, wasReconnecting) => {
      if (wasReconnecting && !isReconnecting) void refresh();
    },
  );

  // Смена фильтра — предикат для live-событий пересчитывается реактивно
  // (computed выше), а серверную выборку нужно перезапросить явно.
  //
  // ⚠ Map чистим СИНХРОННО, до запроса: её содержимое принадлежит ПРОШЛОЙ
  // выборке, а предикат уже новый — live-событие, пришедшее раньше снимка,
  // легло бы к чужим строкам, и наружу уехал бы МИКС двух выборок (боевой
  // репорт 01.08.2026: переписка двух клиентов в одной ленте панели —
  // «липкий» набор `useChatHistory` такой микс потом уже не отпускает).
  // Epoch-guard ниже от этого не спасает: он отсекает устаревший ОТВЕТ, а не
  // устаревшее содержимое Map. Цена — пустой список на время round-trip'а;
  // чужие строки под новым фильтром хуже, чем спиннер.
  if (opts.filter) {
    watch(opts.filter, () => {
      byId.clear();
      rebuildRows();
      void refresh();
    });
  }

  const pollMs = opts.pollMs ?? 60_000;
  const pollTimer = setInterval(() => void refresh(), pollMs);

  onScopeDispose(() => {
    clearInterval(pollTimer);
    unsubscribeEvents?.();
  });

  return {
    rows,
    byId: computed(() => new Map(rows.value.map((row) => [row.id, row] as const))),
    loading,
    error,
    firstLoad,
    refresh,
  };
};
